import { logoPrincipal } from '../assets';
import { useEffect, useRef, useState } from 'react';

export default function Services() {
  // ✅ Animaciones de entrada (fade/slide) con delay base 500ms
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  const baseDelay = 500;
  const ease = 'ease-[cubic-bezier(0.16,1,0.3,1)]';

  return (
    <section
      ref={sectionRef}
      // ✅ antes: py-32 (mucho espacio arriba y abajo)
      // ✅ ahora: más compacto y SIN padding inferior grande
      className="pt-16 pb-0 relative bg-[#EEF2F7] dark:bg-slate-950 overflow-hidden"
      id="services"
    >
      <div
        className="absolute inset-0 w-full h-full opacity-10 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233662E2' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px',
          animation: 'spin 60s linear infinite',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#EEF2F7] via-transparent to-[#EEF2F7] dark:from-slate-950 dark:to-slate-950 pointer-events-none z-10" />

      {/* ✅ logoPrincipal watermark */}
      <img
        src={logoPrincipal}
        alt=""
        className="absolute inset-0 w-full h-full object-contain opacity-[0.06] dark:opacity-[0.10] pointer-events-none z-0"
        style={{
          transform: 'scale(1.25)',
          filter: 'grayscale(1) contrast(1.05)',
        }}
      />
      <div className="absolute inset-0 bg-white/10 dark:bg-black/20 pointer-events-none z-0" />

      <div className="max-w-7xl mx-auto px-6 relative z-20">
        {/* Header */}
        <div
          className={[
            // ✅ antes: mb-20 (se perdía el título visualmente y empujaba el grid muy abajo)
            'flex flex-col md:flex-row justify-between items-end mb-10 gap-8',
            'transform-gpu transition-all duration-[1000ms]',
            ease,
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
          ].join(' ')}
          style={{ transitionDelay: `${baseDelay}ms` }}
        >
          <div className="max-w-2xl">
            {/* ✅ Ajuste: más contraste para que el título “se note” */}
            <h2 className="text-5xl md:text-7xl font-display font-bold text-secondary dark:text-white tracking-tight leading-none">
              Nuestros Servicios
            </h2>
          </div>

          <p className="text-slate-500 dark:text-slate-300 text-lg font-light max-w-sm text-right md:text-right border-r-2 border-[#3662E2] pr-6">
            Desarrollamos estrategias para que tu marca se haga notar y compita en cualquier mercado
          </p>
        </div>

        {/* Grid */}
        <div
          className={[
            'grid grid-cols-1 md:grid-cols-12 gap-8 auto-rows-[minmax(180px,auto)]',
            'transform-gpu transition-all duration-[1100ms]',
            ease,
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8',
          ].join(' ')}
          style={{ transitionDelay: `${baseDelay + 500}ms` }}
        >
          {/* Branding Card */}
          <div className="group relative col-span-1 md:col-span-7 row-span-2 bento-card rounded-[2rem] p-10 flex flex-col justify-between hover:z-30 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#3662E2]/8 via-transparent to-cyan-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#3662E2] opacity-5 blur-[80px] rounded-full group-hover:opacity-15 transition-all duration-700 group-hover:scale-110 dark:opacity-10" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-cyan-400 opacity-0 blur-[60px] rounded-full group-hover:opacity-10 transition-all duration-700 group-hover:scale-105" />

            <div className="relative z-10 flex justify-between items-start">
              <div className="relative w-24 h-24 rounded-2xl flex items-center justify-center border border-white/40 dark:border-white/10 bg-gradient-to-br from-[#3662E2]/20 via-white/70 to-cyan-200/30 dark:from-[#3662E2]/25 dark:via-slate-950/40 dark:to-cyan-400/10 shadow-lg group-hover:shadow-[0_0_40px_rgba(54,98,226,0.45)] transition-all duration-500 group-hover:scale-105 group-hover:rotate-3">
                <div className="absolute inset-0 rounded-2xl ring-1 ring-[#3662E2]/20 dark:ring-[#3662E2]/25 group-hover:ring-[#3662E2]/40 transition-all duration-500" />
                <span className="material-symbols-outlined text-5xl bg-gradient-to-b from-[#3662E2] to-cyan-400 bg-clip-text text-transparent drop-shadow-[0_10px_18px_rgba(54,98,226,0.25)] group-hover:drop-shadow-[0_15px_25px_rgba(54,98,226,0.35)] transition-all duration-500">
                  deployed_code
                </span>
              </div>

              <div className="flex flex-col items-end gap-2">
                <span className="material-symbols-outlined text-[#3662E2]/40 group-hover:text-primary transition-all duration-300 rotate-[-45deg] group-hover:rotate-0 text-3xl">
                  person
                </span>
                <div className="w-2 h-2 rounded-full bg-[#3662E2] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>

            <div className="relative z-10 mt-12">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px bg-gradient-to-r from-transparent via-[#3662E2]/50 to-transparent flex-1" />
                <span className="text-xs font-medium text-[#3662E2]/60 uppercase tracking-widest">Branding</span>
                <div className="h-px bg-gradient-to-r from-[#3662E2]/50 to-transparent flex-1" />
              </div>
              <h3
                id="branding-heading"
                className="text-4xl md:text-5xl font-display font-bold text-secondary dark:text-slate-100 mb-4 group-hover:translate-x-2 transition-transform duration-300 bg-gradient-to-r from-secondary to-[#3662E2] bg-clip-text text-transparent"
              >
                Creamos la base de tu marca.
              </h3>
              <p className="text-slate-500 dark:text-slate-300 text-lg font-light max-w-md mb-6 group-hover:text-secondary dark:group-hover:text-slate-100 transition-colors leading-relaxed">
                Diseñamos la imagen y la identidad de tu marca para que se vea profesional y funcione igual de bien en redes, páginas web y materiales impresos.
              </p>

              <ul className="flex flex-wrap gap-3">
                <li className="px-4 py-2 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm group-hover:bg-[#3662E2] group-hover:text-white group-hover:shadow-lg group-hover:scale-105 transition-all duration-300">
                  Diseño de logo e imagen profesional.
                </li>
                <li className="px-4 py-2 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm group-hover:bg-[#3662E2] group-hover:text-white group-hover:shadow-lg group-hover:scale-105 transition-all duration-300 delay-75">
                  Guía clara para utilizar tu marca correctamente.
                </li>
                <li className="px-4 py-2 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm group-hover:bg-[#3662E2] group-hover:text-white group-hover:shadow-lg group-hover:scale-105 transition-all duration-300 delay-150">
                  Te ayudamos a elegir el nombre ideal.
                </li>
                <li className="px-4 py-2 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm group-hover:bg-[#3662E2] group-hover:text-white group-hover:shadow-lg group-hover:scale-105 transition-all duration-300 delay-225">
                  Hacemos que tu marca se destaque en su mercado.
                </li>
              </ul>
            </div>
          </div>

          {/* Development Card */}
          <div className="group relative col-span-1 md:col-span-5 row-span-1 bento-card rounded-[2rem] p-8 flex flex-col justify-center hover:z-30">
            <div className="relative z-10 flex items-center gap-6">
              <div className="relative w-16 h-16 rounded-xl flex items-center justify-center border border-white/40 dark:border-white/10 bg-gradient-to-br from-indigo-500/20 via-white/70 to-emerald-200/30 dark:from-indigo-500/25 dark:via-slate-950/40 dark:to-emerald-400/10 shadow-sm group-hover:shadow-[0_0_26px_rgba(99,102,241,0.35)] transition-all">
                <div className="absolute inset-0 rounded-xl ring-1 ring-indigo-500/20 dark:ring-indigo-400/20" />
                <span className="material-symbols-outlined text-4xl bg-gradient-to-b from-indigo-500 to-emerald-400 bg-clip-text text-transparent">
                  code
                </span>
              </div>

              <div>
                <h3 className="text-2xl font-display font-bold text-secondary dark:text-slate-100 mb-1 group-hover:text-[#3662E2] transition-colors">
                  Páginas web profesionales.
                </h3>
                <p className="text-slate-400 dark:text-slate-300 text-sm font-light group-hover:text-slate-600 dark:group-hover:text-slate-200">
                  Creamos páginas web modernas, rápidas y pensadas para que tu negocio venda más.
                </p>

                <ul className="flex flex-wrap gap-3 mt-4">
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300">
                    Páginas para empresas.
                  </li>
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-75">
                    Tiendas online listas para vender.
                  </li>
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-150">
                    Páginas para vender más.
                  </li>
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-225">
                    Diseño atractivo y fácil de usar.
                  </li>
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-300">
                    Conectamos tu web con otras herramientas.
                  </li>
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-300">
                    Mejoramos tu página para que aparezca en Google.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Marketing Card */}
          <div className="group relative col-span-1 md:col-span-5 row-span-1 bento-card rounded-[2rem] p-8 flex flex-col justify-center hover:z-30">
            <div className="relative z-10 flex items-center gap-6">
              <div className="relative w-16 h-16 rounded-xl flex items-center justify-center border border-white/40 dark:border-white/10 bg-gradient-to-br from-pink-500/20 via-white/70 to-amber-200/30 dark:from-pink-500/25 dark:via-slate-950/40 dark:to-amber-400/10 shadow-sm group-hover:shadow-[0_0_26px_rgba(236,72,153,0.35)] transition-all">
                <div className="absolute inset-0 rounded-xl ring-1 ring-pink-500/20 dark:ring-pink-400/20" />
                <span className="material-symbols-outlined text-4xl bg-gradient-to-b from-pink-500 to-amber-400 bg-clip-text text-transparent">
                  query_stats
                </span>
              </div>

              <div>
                <h3 className="text-2xl font-display font-bold text-secondary dark:text-slate-100 mb-1 group-hover:text-[#3662E2] transition-colors">
                  Marketing Digital.
                </h3>
                <p className="text-slate-400 dark:text-slate-300 text-sm font-light group-hover:text-slate-600 dark:group-hover:text-slate-200">
                  Hacemos crecer tu marca en internet.
                </p>

                <ul className="flex flex-wrap gap-3 mt-4">
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300">
                    Manejo de redes sociales.
                  </li>
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-75">
                    Segmentación objetiva.
                  </li>
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-150">
                    Remarketing avanzado.
                  </li>
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-225">
                    Campañas multiplataforma y automatizaciones.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* UI/UX Card */}
          <div className="group relative col-span-1 md:col-span-12 row-span-2 bento-card rounded-[2rem] p-10 flex flex-col md:flex-row items-center gap-8 hover:z-30">
            <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-[#3662E2] opacity-5 blur-[80px] rounded-full group-hover:opacity-10 transition-opacity duration-500 dark:opacity-10" />

            <div className="flex-1 relative z-10 order-2 md:order-1">
              <div className="relative w-24 h-24 rounded-2xl flex items-center justify-center border border-white/40 dark:border-white/10 bg-gradient-to-br from-cyan-500/20 via-white/70 to-violet-200/30 dark:from-cyan-500/25 dark:via-slate-950/40 dark:to-violet-400/10 shadow-sm group-hover:shadow-[0_0_30px_rgba(34,211,238,0.35)] transition-all mb-8">
                <div className="absolute inset-0 rounded-2xl ring-1 ring-cyan-500/20 dark:ring-cyan-400/20" />
                <span className="material-symbols-outlined text-5xl bg-gradient-to-b from-cyan-500 to-violet-400 bg-clip-text text-transparent">
                  select_window
                </span>
              </div>

              <h3 className="text-4xl font-display font-bold text-secondary dark:text-slate-100 mb-4 group-hover:translate-x-2 transition-transform duration-300">
                Diseñamos tu app o sistema antes de programarlo.
              </h3>
              <p className="text-slate-500 dark:text-slate-300 text-lg font-light mb-6 group-hover:text-secondary dark:group-hover:text-slate-100 transition-colors">
                Creamos el plano visual y funcional de tu proyecto para que se vea profesional, sea fácil de usar y esté listo para desarrollarse sin errores.
              </p>

              <ul className="flex flex-wrap gap-3">
                <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300">
                  Estructura clara.
                </li>
                <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-75">
                  Diseño profesional.
                </li>
                <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-150">
                  Experiencia optimizada.
                </li>
              </ul>
            </div>

            <div className="flex-1 h-full w-full relative order-1 md:order-2 hidden md:block">
              <div className="w-full h-full min-h-[220px] rounded-xl border border-[#3662E2]/10 dark:border-slate-700/50 bg-white/80 dark:bg-slate-950/60 backdrop-blur-md p-4 shadow-xl overflow-hidden">
                <video className="w-full h-full object-cover rounded-lg" autoPlay loop muted playsInline>
                  <source src="/backgrounds/1038-142623914_tiny.mp4" type="video/mp4" />
                  <source src="/backgrounds/1038-142623914_tiny.webm" type="video/webm" />
                  Tu navegador no soporta videos.
                </video>
              </div>
            </div>
          </div>

          {/* 04. Producción Audiovisual */}
          <div className="group relative col-span-1 md:col-span-6 row-span-1 bento-card rounded-[2rem] p-8 flex flex-col justify-center hover:z-30">
            <div className="relative z-10 flex items-center gap-6">
              <div className="relative w-16 h-16 rounded-xl flex items-center justify-center border border-white/40 dark:border-white/10 bg-gradient-to-br from-rose-500/20 via-white/70 to-orange-200/30 dark:from-rose-500/25 dark:via-slate-950/40 dark:to-orange-400/10 shadow-sm group-hover:shadow-[0_0_26px_rgba(244,63,94,0.35)] transition-all">
                <div className="absolute inset-0 rounded-xl ring-1 ring-rose-500/20 dark:ring-rose-400/20" />
                <span className="material-symbols-outlined text-4xl bg-gradient-to-b from-rose-500 to-orange-400 bg-clip-text text-transparent">
                  video_camera_front
                </span>
              </div>

              <div className="flex-1">
                <h3 className="text-2xl font-display font-bold text-secondary dark:text-slate-100 mb-2 group-hover:text-[#3662E2] transition-colors">
                  Editamos tus videos con calidad profesional.
                </h3>
                <p className="text-slate-500 dark:text-slate-300 text-lg font-light mb-6 group-hover:text-secondary dark:group-hover:text-slate-100 transition-colors">
                  Editamos tus videos con calidad profesional.
                </p>

                <ul className="flex flex-wrap gap-3 mt-4">
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300">
                    Usamos animaciones con IA.
                  </li>
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-75">
                    Creamos personajes digitales para tu marca.
                  </li>
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-150">
                    Animaciones con IA.
                  </li>
                  <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-225">
                    Avatares digitales.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 06. Inteligencia Artificial Aplicada */}
          <div className="group relative col-span-1 md:col-span-6 row-span-1 bento-card rounded-[2rem] p-8 flex flex-col justify-center hover:z-30">
            <div className="relative z-10 flex items-center gap-6">
              <div className="relative w-16 h-16 rounded-xl flex items-center justify-center border border-white/40 dark:border-white/10 bg-gradient-to-br from-emerald-500/20 via-white/70 to-sky-200/30 dark:from-emerald-500/25 dark:via-slate-950/40 dark:to-sky-400/10 shadow-sm group-hover:shadow-[0_0_26px_rgba(16,185,129,0.35)] transition-all">
                <div className="absolute inset-0 rounded-xl ring-1 ring-emerald-500/20 dark:ring-emerald-400/20" />
                <span className="material-symbols-outlined text-4xl bg-gradient-to-b from-emerald-500 to-sky-400 bg-clip-text text-transparent">
                  neurology
                </span>
              </div>

              <div className="flex-1">
                <h3 className="text-2xl font-display font-bold text-secondary dark:text-slate-100 mb-2 group-hover:text-[#3662E2] transition-colors">
                  Inteligencia artificial para tu negocio.
                </h3>

                <ul className="text-slate-400 dark:text-slate-300 text-sm font-light space-y-1">
                  <li>Automatizamos tareas para que ahorres tiempo.</li>
                  <br />

                  <ul className="flex flex-wrap gap-3 mt-4">
                    <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300">
                      Creamos sistemas digitales que organizan tu negocio.
                    </li>
                    <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-75">
                      Integramos herramientas de inteligencia artificial en tu empresa.
                    </li>
                    <li className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300 delay-150">
                      Mejoramos tus procesos para que trabajes más rápido y ganes más.
                    </li>
                  </ul>
                </ul>
              </div>
            </div>
          </div>

          {/* 05. Producción Audiovisual & Fotografía Profesional */}
          <div className="group relative col-span-1 md:col-span-8 row-span-2 bento-card rounded-[2rem] p-10 flex flex-col justify-between hover:z-30">
            <div className="absolute -top-16 -left-16 w-80 h-80 bg-[#3662E2] opacity-5 blur-[80px] rounded-full group-hover:opacity-10 transition-opacity duration-700 dark:opacity-10" />

            <div className="relative z-10 flex items-center gap-6">
              <div className="relative w-24 h-24 rounded-2xl flex items-center justify-center border border-white/40 dark:border-white/10 bg-gradient-to-br from-amber-500/20 via-white/70 to-fuchsia-200/30 dark:from-amber-500/25 dark:via-slate-950/40 dark:to-fuchsia-400/10 shadow-sm group-hover:shadow-[0_0_30px_rgba(245,158,11,0.35)] transition-all">
                <div className="absolute inset-0 rounded-2xl ring-1 ring-amber-500/20 dark:ring-amber-400/20" />
                <span className="material-symbols-outlined text-5xl bg-gradient-to-b from-amber-500 to-fuchsia-400 bg-clip-text text-transparent">
                  photo_camera
                </span>
              </div>

              <div className="flex-1">
                <h3 className="text-4xl font-display font-bold text-secondary dark:text-slate-100 group-hover:text-[#3662E2] transition-colors">
                  Producción de video y fotografía profesional.
                </h3>
                <p className="text-slate-500 dark:text-slate-300 text-lg font-light mt-2">
                  Creamos fotos y videos que hacen que tu marca se vea grande, profesional y lista para competir.
                </p>
              </div>
            </div>

            <div className="relative z-10 mt-10">
              <ul className="flex flex-wrap gap-3">
                {[
                  'Fotos profesionales para que tu negocio se vea más confiable.',
                  'Fotos de productos que venden.',
                  'Sesiones para emprendedores que quieren proyectar autoridad.',
                ].map((item) => (
                  <li
                    key={item}
                    className="px-3 py-1 rounded-full border border-[#3662E2]/20 text-xs font-medium text-[#3662E2] bg-white dark:bg-slate-950 group-hover:bg-[#3662E2] group-hover:text-white transition-colors duration-300"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA Card */}
          <div
            className="col-span-1 md:col-span-4 row-span-2 bento-card rounded-[2rem] overflow-hidden relative flex flex-col items-center justify-center text-center p-8 border-[#3662E2]/20 bg-[#3662E2] group hover:bg-[#2D54C7] transition-all duration-500 cursor-pointer shadow-lg hover:shadow-[#3662E2]/40"
            role="button"
            tabIndex={0}
            onClick={() => {
              const el = document.getElementById('contact');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const el = document.getElementById('contact');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvc3ZnPg==')] opacity-10 mix-blend-overlay"></div>

            <div className="relative z-10">
              <div className="w-24 h-24 mx-auto rounded-full bg-white/20 flex items-center justify-center mb-6 backdrop-blur-sm group-hover:bg-white/30 energy-pulse border border-white/30">
                <span className="material-symbols-outlined text-4xl text-white group-hover:rotate-12 transition-transform duration-300">
                  rocket_launch
                </span>
              </div>
              <h3 className="text-3xl font-bold text-white mb-2 tracking-tight">
                Es momento de hacer crecer tu marca.
              </h3>
              <p className="text-sm text-blue-100/90 mb-8 font-light">Estamos listos para ayudarte a lograrlo.</p>

              <button
                className="inline-flex items-center gap-3 text-xs font-black tracking-[0.2em] uppercase text-slate-950 bg-lime-400 hover:bg-lime-500 px-6 py-3 rounded-full transition-all hover:shadow-[0_4px_18px_rgba(163,230,53,0.45)]"
                role="button"
                tabIndex={0}
                onClick={() => {
                  const el = document.getElementById('contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const el = document.getElementById('contact');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Agenda una asesoría
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}